import type { QueryClient } from '@tanstack/react-query';
import type { LoaderFunction, LoaderFunctionArgs, RouteObject } from 'react-router-dom';

import { sessionQueryOptions } from '../../session/queryOptions';
import { meAccessFrom } from '../api/normalizeMe';
import { ACCESS_POLICY } from '../policy';
import type { AccessPolicy } from '../policy';
import { meAccessQueryOptions } from '../queryOptions';

import { decideBranch, routeAccessOf } from './routeAccess';

const declaresAccess = (handles: readonly unknown[]): boolean => handles.some((handle) => routeAccessOf(handle) !== undefined);

const isSignedIn = async (queryClient: QueryClient): Promise<boolean> => {
  try {
    return (await queryClient.ensureQueryData(sessionQueryOptions())).status === 'signedIn';
  } catch {
    return false;
  }
};

const readAccess = async (queryClient: QueryClient) => {
  try {
    return await queryClient.fetchQuery(meAccessQueryOptions());
  } catch {
    return null;
  }
};

const mayOpen = async (queryClient: QueryClient, handles: readonly unknown[], { params, request }: LoaderFunctionArgs): Promise<boolean> => {
  const payload = await readAccess(queryClient);
  if (payload === null) return true;
  const searchParams = new URL(request.url).searchParams;
  return decideBranch(meAccessFrom(payload), handles.map((handle) => ({ handle, params })), searchParams).decision.allow;
};

const guarded = (original: LoaderFunction, handles: readonly unknown[], queryClient: QueryClient, policy: AccessPolicy): LoaderFunction => {
  if (policy !== 'enforce' || !declaresAccess(handles)) return original;
  return async (args, context) => {
    if (!(await isSignedIn(queryClient))) return null;
    return (await mayOpen(queryClient, handles, args)) ? original(args, context) : null;
  };
};

const walk = (routes: readonly RouteObject[], ancestors: readonly unknown[], queryClient: QueryClient, policy: AccessPolicy): RouteObject[] =>
  routes.map((route): RouteObject => {
    const handles = [...ancestors, route.handle];
    const loader = typeof route.loader === 'function' ? guarded(route.loader, handles, queryClient, policy) : route.loader;
    if (route.index) return { ...route, loader };
    const children = route.children ? walk(route.children, handles, queryClient, policy) : undefined;
    return { ...route, loader, children };
  });

export const guardLoaders = (routes: readonly RouteObject[], queryClient: QueryClient, policy: AccessPolicy = ACCESS_POLICY): RouteObject[] =>
  walk(routes, [], queryClient, policy);
